import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isBefore, startOfToday, addMonths, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, X, Clock, Calendar, Trash2 } from 'lucide-react';
import { apiGet, apiPost, apiDelete, convertLocalTimeToEpoch, convertEpochToLocalTime, formatTimeRange, calculateDuration } from '../../utils/api';
import { Button, Card, Input, Select, Spinner } from '../design-system';

const Availability = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);

  const { register, handleSubmit, reset, formState: { errors }, watch } = useForm();
  const isRecurring = watch('isRecurring');

  // Generate time options (24-hour format for easier epoch conversion)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push({ value: timeString, label: timeString });
    }
  }

  const fetchCalendarEvents = async () => {
    try {
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      const url = `/.netlify/functions/get-calendar?date=${dateString}`;
      
      console.log('[Availability] Fetching calendar events:', {
        url,
        dateString,
        selectedDate: selectedDate.toISOString()
      });
      
      const response = await apiGet(url);
      
      console.log('[Availability] Calendar events response:', {
        eventCount: response?.events?.length || 0,
        hasEvents: !!response?.events
      });
      
      // Filter events to only show those that actually fall on the selected date in local time
      // This ensures timezone accuracy - events are queried by epoch but filtered by local date
      const filteredEvents = (response.events || []).filter(event => {
        // Convert epoch timestamp to local date
        const eventStartDate = new Date(event.startEpoch * 1000);
        const eventEndDate = new Date(event.endEpoch * 1000);
        
        // Get the target date boundaries in local time
        const targetDateStart = new Date(format(selectedDate, 'yyyy-MM-dd') + 'T00:00:00');
        const targetDateEnd = new Date(format(selectedDate, 'yyyy-MM-dd') + 'T23:59:59');
        
        // Check if any part of this event falls on the target date in local time
        return (eventStartDate < targetDateEnd && eventEndDate > targetDateStart);
      });
      
      console.log('[Availability] Filtered events:', {
        originalCount: response?.events?.length || 0,
        filteredCount: filteredEvents.length
      });
      
      setCalendarEvents(filteredEvents);
    } catch (error) {
      console.error('[Availability] Error fetching calendar events:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        dateString: format(selectedDate, 'yyyy-MM-dd'),
        selectedDate: selectedDate.toISOString()
      });
      toast.error('Failed to load calendar events');
    }
  };

  useEffect(() => {
    fetchCalendarEvents();
  }, [selectedDate]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Validate time inputs
      if (data.startTime >= data.endTime) {
        toast.error('End time must be after start time');
        return;
      }
      
      // Convert local date and times to epoch timestamps using utility function
      const startEpoch = convertLocalTimeToEpoch(data.startTime, selectedDate);
      const endEpoch = convertLocalTimeToEpoch(data.endTime, selectedDate);

      // Additional validation
      if (startEpoch >= endEpoch) {
        toast.error('Invalid time range');
        return;
      }

      // Check if the selected time is in the past
      const now = Math.floor(Date.now() / 1000);
      if (startEpoch < now) {
        toast.error('Cannot set availability for past times');
        return;
      }

      // Validate recurring availability
      if (data.isRecurring) {
        if (!data.recurringEndDate) {
          toast.error('End date is required for recurring availability');
          return;
        }
        if (!data.recurringDays || data.recurringDays.length === 0) {
          toast.error('Please select at least one day for recurring availability');
          return;
        }
        // Validate end date is after start date
        const endDate = new Date(data.recurringEndDate);
        if (endDate < selectedDate) {
          toast.error('Recurring end date must be after the start date');
          return;
        }
      }

      const availabilityData = {
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: startEpoch, // Send epoch timestamp
        endTime: endEpoch, // Send epoch timestamp
        isRecurring: data.isRecurring || false,
        recurringDays: data.recurringDays || [],
        recurringEndDate: data.recurringEndDate ? format(new Date(data.recurringEndDate), 'yyyy-MM-dd') : null
      };

      await apiPost('/.netlify/functions/set-availability', availabilityData);
      toast.success('Availability set successfully');
      reset();
      setShowAddForm(false);
      fetchCalendarEvents();
    } catch (error) {
      console.error('Error setting availability:', error);
      if (error.message.includes('Invalid time format')) {
        toast.error('Please select valid start and end times');
      } else if (error.message.includes('Network error')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(error.message || 'Failed to set availability');
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteAvailability = async (eventId) => {
    try {
      await apiDelete('/.netlify/functions/delete-availability', { eventId });
      toast.success('Availability deleted successfully');
      fetchCalendarEvents();
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast.error('Failed to delete availability');
    }
  };

  const generateCalendarDays = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days to align with calendar grid
    const startDay = start.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const paddingStart = Array.from({ length: startDay }, (_, i) => null);
    
    return [...paddingStart, ...days];
  };

  const isPastDate = (date) => {
    return isBefore(date, startOfToday());
  };

  const handleMonthChange = (direction) => {
    if (direction === 'next') {
      setCurrentMonth(addMonths(currentMonth, 1));
    } else {
      setCurrentMonth(subMonths(currentMonth, 1));
    }
  };

  const getAvailabilityEvents = () => {
    return calendarEvents.filter(event => event.type === 'available');
  };

  const getBookedEvents = () => {
    return calendarEvents.filter(event => event.type === 'booked');
  };

  const getBlockedEvents = () => {
    return calendarEvents.filter(event => event.type === 'blocked');
  };

  // Using utility functions for consistent timezone handling
  const formatEpochToLocalTime = (epoch) => {
    return convertEpochToLocalTime(epoch);
  };

  const formatTimeRangeLocal = (startEpoch, endEpoch) => {
    return formatTimeRange(startEpoch, endEpoch);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
          <h2 className="text-base md:text-lg font-medium text-gray-900">Manage Availability</h2>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            variant="primary"
            size="medium"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Availability
          </Button>
        </div>
        
        {/* Calendar */}
        <div className="max-w-md mx-auto">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Select Date</h3>
          
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={() => handleMonthChange('prev')}
              variant="secondary"
              size="small"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h4 className="text-base md:text-lg font-medium text-gray-900">
              {format(currentMonth, 'MMMM yyyy')}
            </h4>
            <Button
              onClick={() => handleMonthChange('next')}
              variant="secondary"
              size="small"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-xs text-gray-500 text-center py-2 font-medium">
                {day}
              </div>
            ))}
            {generateCalendarDays().map((date, index) => (
              <div key={index} className="p-1">
                {date ? (
                  <button
                    onClick={() => setSelectedDate(date)}
                    disabled={isPastDate(date)}
                    className={`w-full p-2 text-sm rounded-md transition-colors duration-200 ${
                      format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
                        ? 'text-white'
                        : isPastDate(date)
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                    style={{
                      backgroundColor: format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
                        ? 'var(--color-primary)'
                        : 'transparent'
                    }}
                  >
                    {format(date, 'd')}
                  </button>
                ) : (
                  <div className="p-2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Current Availability */}
      <Card>
        <h3 className="text-base md:text-lg font-medium text-gray-900 mb-4">
          Availability for {format(selectedDate, 'MMMM d, yyyy')}
        </h3>
        
        {loading ? (
          <div className="text-center py-8">
            <Spinner size="medium" />
          </div>
        ) : getAvailabilityEvents().length === 0 ? (
          <p className="text-gray-500 text-center py-8">No availability set for this date</p>
        ) : (
          <div className="space-y-4">
            {getAvailabilityEvents().map((event) => (
              <div
                key={event._id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5" style={{ color: 'var(--color-primary)' }} />
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatTimeRangeLocal(event.startEpoch, event.endEpoch)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {event.isRecurring ? (
                        <>
                          Recurring: {event.recurringDays?.join(', ') || 'No days specified'}
                          {event.recurringEndDate && ` • Ends: ${format(new Date(event.recurringEndDate + 'T00:00:00'), 'MMM d, yyyy')}`}
                        </>
                      ) : (
                        'One-time availability'
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => deleteAvailability(event._id)}
                  variant="error"
                  size="small"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Current Bookings */}
      {getBookedEvents().length > 0 && (
        <Card>
          <h3 className="text-base md:text-lg font-medium text-gray-900 mb-4">
            Bookings for {format(selectedDate, 'MMMM d, yyyy')}
          </h3>
          
          <div className="space-y-4">
            {getBookedEvents().map((event) => (
              <div
                key={event._id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5" style={{ color: 'var(--color-success)' }} />
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatTimeRangeLocal(event.startEpoch, event.endEpoch)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {event.patientName} • {event.duration} minutes
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Blocked Time */}
      {getBlockedEvents().length > 0 && (
        <Card>
          <h3 className="text-base md:text-lg font-medium text-gray-900 mb-4">
            Blocked Time for {format(selectedDate, 'MMMM d, yyyy')}
          </h3>
          
          <div className="space-y-4">
            {getBlockedEvents().map((event) => (
              <div
                key={event._id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                style={{ backgroundColor: 'rgba(232, 112, 112, 0.1)' }}
              >
                <div className="flex items-center space-x-3">
                  <X className="h-5 w-5" style={{ color: 'var(--color-error)' }} />
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatTimeRangeLocal(event.startEpoch, event.endEpoch)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {event.reason || 'Blocked time'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

       {/* Add Availability Modal */}
       {showAddForm && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
           <Card className="relative w-full max-w-md mx-auto" variant="elevated">
             <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add Availability</h3>
                <Button
                  onClick={() => {
                    setShowAddForm(false);
                    reset();
                  }}
                  variant="secondary"
                  size="small"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
              
              <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(44, 166, 164, 0.1)' }}>
                <p className="text-sm" style={{ color: 'var(--color-primary)' }}>
                  <strong>Selected Date:</strong> {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <select
                      {...register('startTime', { required: 'Start time is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(44, 166, 164, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '';
                        e.target.style.boxShadow = '';
                      }}
                    >
                      <option value="">Select start time</option>
                      {timeOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {errors.startTime && (
                      <p className="mt-1 text-sm" style={{ color: 'var(--color-error)' }}>{errors.startTime.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <select
                      {...register('endTime', { required: 'End time is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(44, 166, 164, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '';
                        e.target.style.boxShadow = '';
                      }}
                    >
                      <option value="">Select end time</option>
                      {timeOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {errors.endTime && (
                      <p className="mt-1 text-sm" style={{ color: 'var(--color-error)' }}>{errors.endTime.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('isRecurring')}
                      className="h-4 w-4 border-gray-300 rounded focus:ring-2"
                      style={{
                        accentColor: 'var(--color-primary)'
                      }}
                    />
                    <span className="ml-2 text-sm text-gray-700">Make this recurring</span>
                  </label>
                </div>

                {isRecurring && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Recurring Days
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                          <label key={day} className="flex items-center">
                            <input
                              type="checkbox"
                              value={day}
                              {...register('recurringDays')}
                              className="h-4 w-4 border-gray-300 rounded focus:ring-2"
                              style={{
                                accentColor: 'var(--color-primary)'
                              }}
                            />
                            <span className="ml-2 text-sm text-gray-700 capitalize">{day}</span>
                          </label>
                        ))}
                      </div>
                      {errors.recurringDays && (
                        <p className="mt-1 text-sm" style={{ color: 'var(--color-error)' }}>
                          {errors.recurringDays.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Recurring End Date
                      </label>
                      <input
                        type="date"
                        {...register('recurringEndDate', {
                          required: isRecurring ? 'End date is required for recurring availability' : false
                        })}
                        min={format(selectedDate, 'yyyy-MM-dd')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--color-primary)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(44, 166, 164, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '';
                          e.target.style.boxShadow = '';
                        }}
                      />
                      {errors.recurringEndDate && (
                        <p className="mt-1 text-sm" style={{ color: 'var(--color-error)' }}>
                          {errors.recurringEndDate.message}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        The recurring availability will end on this date
                      </p>
                    </div>
                  </>
                )}

                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      reset();
                    }}
                    variant="secondary"
                    size="medium"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    variant="primary"
                    size="medium"
                    className="flex-1"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Add Availability'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Availability; 